import React, { useEffect, useMemo, useState } from 'react';
import apiService, { API_BASE_URL, Product, toImageSrc } from '../services/api';
import { useSEO } from '../hooks/useSEO';
import HeroSection from './home/HeroSection';
import BrandStorySection from './home/BrandStorySection';
import CategoryShowcase, { Category } from './home/CategoryShowcase';
import CollectionShowcase, { CollectionItem } from './home/CollectionShowcase';
import ProductShowcase from './home/ProductShowcase';
import SocialProofSection from './home/SocialProofSection';
import EditorialTeaser, { EditorialItem } from './home/EditorialTeaser';
import HomeSEOContent from './home/HomeSEOContent';
import { marketingService } from '../services/marketingService';

type CollectionBlueprint = Omit<CollectionItem, 'image'> & { categorySlug?: string };

const COLLECTION_BLUEPRINTS: CollectionBlueprint[] = [
  {
    title: 'Konstblommor & plastblommor',
    description: 'Säsongslösa arrangemang i konstmaterial – perfekta för långvarig stämning i hemmet.',
    href: '/produkter?category=konstvaxter',
    accent: 'rgba(212, 154, 115, 0.45)',
    categorySlug: 'konstvaxter'
  },
  {
    title: 'Ljus & dofter',
    description: 'Doftljus, doftpinnar och ljuslyktor för att skapa en mjuk, välkomnande atmosfär.',
    href: '/produkter?kategori=doftljus-och-doftpinnar',
    accent: 'rgba(117, 133, 123, 0.35)',
    categorySlug: 'doftljus-och-doftpinnar'
  },
  {
    title: 'Present att ta med',
    description: 'Omtänksamma gåvor till middagsvärden – från praliner till vackra brickor.',
    href: '/produkter?kategori=dukning-och-servering',
    accent: 'rgba(200, 173, 143, 0.35)',
    categorySlug: 'dukning-och-servering'
  },
  {
    title: 'Mjuka textilier',
    description: 'Kuddar, plädar och kökshanddukar som adderar värme till varje rum.',
    href: '/produkter?kategori=textiler',
    accent: 'rgba(174, 159, 148, 0.35)',
    categorySlug: 'textiler'
  }
];

const HOME_CATEGORY_SLUGS = [
  'doftljus-och-doftpinnar',
  'ljuslyktor-och-dekoration',
  'lyxigt-godis-och-annat',
  'muggar-och-koppar',
  'dukning-och-servering',
  'textiler'
] as const;

type HomeCategorySlug = typeof HOME_CATEGORY_SLUGS[number];

const isHomeCategorySlug = (slug: string): slug is HomeCategorySlug =>
  HOME_CATEGORY_SLUGS.includes(slug as HomeCategorySlug);

const resolveImageUrl = (src?: string) => {
  if (!src) return undefined;
  const stringSrc = String(src);
  if (stringSrc.startsWith('http://') || stringSrc.startsWith('https://')) {
    return stringSrc;
  }
  if (stringSrc.startsWith('/')) {
    const baseHost = API_BASE_URL.replace(/\/api$/i, '');
    return `${baseHost}${stringSrc}`;
  }
  return toImageSrc(stringSrc);
};

const Home: React.FC = () => {
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  const [newArrivals, setNewArrivals] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [editorialItems, setEditorialItems] = useState<EditorialItem[]>([]);

  useSEO({
    entityType: 'page',
    entityId: 'home',
    fallbackTitle: 'Lilla Flora – Floristik & inredning i Kramfors',
    fallbackDescription:
      'Lilla Flora i Kramfors erbjuder floristbundna buketter, hållbara presenter och utvald inredning. Välkommen till vår webshop och butik.',
    fallbackImage: '/static/images/lillaflora-hero.jpg'
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        const [featuredResponse, latestResponse, categoriesResponse, marketingResponse] = await Promise.all([
          apiService.getProducts({ featured: true, limit: 16 }),
          apiService.getProducts({ limit: 24 }),
          apiService.getCategories(),
          marketingService.getAll().catch(() => [])
        ]);

        const filterInStock = (products: Product[]) =>
          (products || []).filter(product => (product.inventory_quantity ?? 0) > 0);

        const nextFeatured = filterInStock(featuredResponse).slice(0, 8);
        const nextNewArrivals = filterInStock(latestResponse)
          .sort((a, b) => {
            const dateA = new Date(a.created_at || '').getTime();
            const dateB = new Date(b.created_at || '').getTime();
            return dateB - dateA;
          })
          .slice(0, 8);

        setFeaturedProducts(nextFeatured);
        setNewArrivals(nextNewArrivals);

        const activeCategories: Category[] = (categoriesResponse || [])
          .filter((category: any) => category?.is_active)
          .map(
            (category: any): Category => ({
              id: category.id,
              name: category.name,
              slug: category.slug,
              description: category.description,
              image_url: category.image_url,
              sort_order: category.sort_order
            })
          );

        setCategories(activeCategories);

        const editorial = Array.isArray(marketingResponse)
          ? marketingResponse
              .filter(page => page?.is_published !== false)
              .slice(0, 3)
              .map(
                (page): EditorialItem => ({
                  id: page.id,
                  title: page.title,
                  excerpt: page.excerpt,
                  slug: page.slug,
                  featured_image: page.featured_image
                })
              )
          : [];

        setEditorialItems(editorial);
      } catch (error) {
        console.error('Error loading home page data:', error);
      }
    };

    loadData();
  }, []);

  const heroImage = useMemo(() => resolveImageUrl('/static/bild1.png'), []);

  const storyImage = useMemo(() => resolveImageUrl('/static/bild2.png'), []);

  const collections = useMemo<CollectionItem[]>(() => {
    const productPool = [...featuredProducts, ...newArrivals];
    return COLLECTION_BLUEPRINTS.map(collection => {
      let resolvedImage: string | undefined;
      const { categorySlug, ...collectionWithoutSlug } = collection;

      if (categorySlug) {
        const categoryMatch = categories.find(category => category.slug === categorySlug);
        if (categoryMatch?.image_url) {
          resolvedImage = resolveImageUrl(categoryMatch.image_url);
        }

        if (!resolvedImage) {
          const productMatch = productPool.find(product =>
            (product.categories || []).some(cat =>
              cat.toLowerCase().includes(categorySlug.replace(/-/g, ' '))
            )
          );
          if (productMatch?.images?.[0]) {
            resolvedImage = resolveImageUrl(productMatch.images[0]);
          }
        }
      }

      if (!resolvedImage) {
        const fallbackProduct = productPool.find(product => product.images && product.images.length);
        if (fallbackProduct?.images?.[0]) {
          resolvedImage = resolveImageUrl(fallbackProduct.images[0]);
        }
      }

      return {
        ...collectionWithoutSlug,
        image: resolvedImage
      };
    });
  }, [categories, featuredProducts, newArrivals]);

  const topCategories = useMemo(() => {
    if (!categories.length) return [];

    const bySlug = HOME_CATEGORY_SLUGS.map(
      slug => categories.find(category => category.slug === slug)
    ).filter(Boolean) as Category[];

    if (bySlug.length >= 6) {
      return bySlug.slice(0, 6);
    }

    const remaining = categories
      .filter(category => !isHomeCategorySlug(category.slug))
      .sort((a, b) => (a.sort_order ?? Number.MAX_SAFE_INTEGER) - (b.sort_order ?? Number.MAX_SAFE_INTEGER));

    return [...bySlug, ...remaining].slice(0, 6);
  }, [categories]);

  return (
    <>
      <HeroSection heroImage={heroImage} />
      <HomeSEOContent />
      <BrandStorySection storyImage={storyImage} />
      <CategoryShowcase categories={topCategories} />
      <CollectionShowcase collections={collections} />
      <ProductShowcase featured={featuredProducts} newArrivals={newArrivals} />
      <SocialProofSection />
      <EditorialTeaser items={editorialItems} />
    </>
  );
};

export default Home;
